import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useAdminDashboard } from "../../../hooks/useAdminDashboard";
import { useStores } from "../../../hooks/useStores";
import { refetchDashboard, updateSelectedStore } from "../../../slices/dashboardSlice";
import CashierLayout from "../Layout";
import type { Store } from "../../../types/store";
import RecentSalesSkeleton from "../../../components/RecentSalesTableSkeleton";
import RecentSalesTable from "../../../components/RecentSalesTable";
import TopSellingProductsSkeleton from "../../../components/TopProductsSkeleton";
import TopSellingProductsTable from "../../../components/TopSellingProductsTable";
import LowStockProductsTable from "../../../components/LowStockProductsTable";
import LowStockProductsSkeleton from "../../../components/LowStockProductsSkeleton";
import { useNavigate } from "react-router-dom";
import { selectOnboardingForRole, selectOnboardingStatus, fetchOnboarding } from "../../../slices/onboardingSlice";

const CashierDashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    recentSales,
    topProducts,
    lowStockProducts,
    loading,
    initialized
  } = useAdminDashboard();

  const { stores } = useStores();

  const selectedStore = useAppSelector(
    (state) => state.dashboard.selectedStore
  );

  const onboarding = useAppSelector(selectOnboardingForRole("Cashier"));
  const onboardingStatus = useAppSelector(selectOnboardingStatus);

  useEffect(() => {
    dispatch(fetchOnboarding("Cashier"));
  }, [dispatch]);

  useEffect(() => {
    if (onboardingStatus !== "succeeded") return;
    if (!onboarding.completedSteps.some(Boolean) && !onboarding.isSkipped) {
      navigate("/onboarding", { replace: true });
    }
  }, [onboardingStatus, onboarding.completedSteps, onboarding.isSkipped, navigate]);

  useEffect(() => {
    dispatch(refetchDashboard());
  }, [selectedStore, dispatch]);

  const showResumeBanner =
    onboarding.isSkipped && !onboarding.completedSteps.every(Boolean);

  return (
    <CashierLayout>
      <div className="p-4 space-y-6">

        {showResumeBanner && (
          <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-4">
            <div>
              <p className="font-medium text-gray-800">Continue setting up your workspace</p>
              <p className="text-sm text-gray-500">You haven't finished the onboarding wizard yet.</p>
            </div>
            <button
              onClick={() => navigate("/onboarding")}
              className="rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90"
            >
              Resume Setup
            </button>
          </div>
        )}

        {/* Store Filter */}
        <div>
          <label htmlFor="store-filter" className="form-label">
            Filter results by store
          </label>
          <select
            id="store-filter"
            value={selectedStore?.id || ""}
            onChange={(e) => {
              const storeId = e.target.value || null;

              const store = 
                storeId === null 
                  ? null
                  : stores.find((store) => store.id === storeId) ?? null;

              dispatch(updateSelectedStore(store));
            }}
            className="form-input md:h-16"
          >
            <option value="">All Stores</option>
            {
              stores.map((store: Store) => 
                <option 
                  key={store.id}
                  value={store.id}
                >
                  {store.name}
                </option>
              )
            }
          </select>
        </div>

        {/* ───────────── RECENT SALES ───────────── */}
        {
          loading.recentSales && !initialized.recentSales ? (
            <RecentSalesSkeleton />
          ) : (
            <RecentSalesTable
              sales={recentSales}
              onViewAll={() => navigate("/history/sales")}
            />
          )
        }

        {/* ───────────── TOP PRODUCTS ───────────── */}
        {
          loading.topProducts && !initialized.topProducts ? (
            <TopSellingProductsSkeleton />
          ) : (
            <TopSellingProductsTable
              // onViewAll={() => navigate("/history/products")}
              products={topProducts}
            />
          )
        }

        {/* ───────────── LOW STOCK PRODUCTS ───────────── */}
        {
          loading.lowStockProducts && !initialized.lowStockProducts ? (
            <LowStockProductsSkeleton />
          ) : (
            <LowStockProductsTable
              onViewAll={() => navigate("/inventory")}
              products={lowStockProducts}
            />
          )
        }
      </div>
    </CashierLayout>
  );
};

export default CashierDashboard;

